<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VButton, VButtonInvisible, VIcon, VLoadingSection } from '@/modules/shared/components'
import BatchTransactionForm from '@/modules/investing/components/entities/transactions/form.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useBatchTransactionStore } from '@/modules/investing/stores/batch-transaction-store'

const { entity_id, entity_type, slug, batch_transaction_id } = useRoute().params as {
  entity_id: string
  entity_type: string
  slug: string
  batch_transaction_id: string
}
const router = useRouter()

const batchTransactionStore = useBatchTransactionStore()
const entityStore = useEntityStore()

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const loading = ref(false)
const formComponent = ref(null)

const entity = computed(() => entityStore.entityExpanded)
const commitments = computed(() => entityStore.listCommitments)
const batchTransaction = computed(() => batchTransactionStore.items.get(batch_transaction_id))

const submit = async (payload) => {
  loading.value = true
  await batchTransactionStore.updateOperatingActivity(batch_transaction_id, payload, { slug })
  router.push({
    name: 'investing.transactions',
    params: { entity_id: entity_id, entity_type: entity_type },
  })
  loading.value = false
}
const triggerFormSubmit = () => {
  if (formComponent.value) formComponent.value.submit()
}

onMounted(async () => {
  showSkeleton()
  await Promise.all([
    batchTransactionStore.fetchBatchTransaction(batch_transaction_id, { slug }),
    entityStore.fetchCommitments(entity_id, { slug }),
  ])
  hideSkeleton()
})
</script>

<template>
  <div class="fixed inset-0 overflow-y-auto bg-white">
    <!-- nav -->
    <div
      class="sticky top-0 z-50 flex items-center justify-between border-b bg-white/50 px-6 py-4 shadow-sm backdrop-blur"
    >
      <div class="left flex items-center space-x-3">
        <RouterLink
          :to="{
            name: 'investing.transactions',
            params: { entity_id: entity_id, entity_type: entity_type },
          }"
        >
          <VButtonInvisible><VIcon name="x" /></VButtonInvisible>
        </RouterLink>
        <h3 class="font-medium leading-7 text-gray-700 dark:text-gray-300">
          Edit {{ formComponent?.transactionTypeLabel || 'Transaction' }}
        </h3>
      </div>
      <div class="right flex items-center space-x-1.5">
        <RouterLink
          :to="{
            name: 'investing.transactions',
            params: { entity_id: entity_id, entity_type: entity_type },
          }"
        >
          <VButton>Cancel</VButton>
        </RouterLink>
        <VButton :loading="loading" variant="primary" class="px-8" @click="triggerFormSubmit">Save</VButton>
      </div>
    </div>
    <!-- main form -->
    <div class="p-6">
      <VLoadingSection
        v-if="skeleton"
        class="mt-40"
        title="Preparing Your Form..."
        description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
      smoothly."
      />
      <BatchTransactionForm
        v-else
        ref="formComponent"
        :batchTransaction="batchTransaction"
        :entity="entity"
        :commitments="commitments"
        @submit="submit"
      />
    </div>
  </div>
</template>
